import {
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import { GC_TIME_MS, STALE_TIME_MS } from "./constants";

type FetchPageFn<T> = (page: number, pageSize: number) => Promise<T[]>;

/**
 * Standalone helper to fetch all pages of a paginated API.
 */
export async function fetchAllPages<T>(
  fetchPage: FetchPageFn<T>,
  pageSize: number = 1000,
): Promise<T[]> {
  const allItems: T[] = [];
  let page = 0;
  let hasMore = true;

  while (hasMore) {
    const results = await fetchPage(page, pageSize);

    if (Array.isArray(results)) {
      allItems.push(...results);
      hasMore = results.length === pageSize;
      page++;
    } else {
      hasMore = false;
    }
  }

  return allItems;
}

interface UseFetchAllOptions<T>
  extends Omit<UseQueryOptions<T[]>, "queryFn" | "queryKey"> {
  pageSize?: number;
}

export function useFetchAll<T>(
  key: unknown[],
  fetchPage: FetchPageFn<T>,
  options: UseFetchAllOptions<T> = {},
): UseQueryResult<T[]> {
  const {
    enabled = true,
    pageSize = 1000,
    staleTime = STALE_TIME_MS,
    gcTime = GC_TIME_MS,
    ...queryOptions
  } = options;

  return useQuery({
    queryKey: [...key],
    queryFn: () => fetchAllPages(fetchPage, pageSize),
    enabled,
    staleTime,
    gcTime,
    ...queryOptions,
  });
}
